/**
 * /expense/portfolio — the finance oversight + settlement screen (ADR-0020
 * Phase 4). Lists org claims across their lifecycle (awaiting reimbursement /
 * reimbursed / paid off-cycle) via the HR/finance-gated list endpoint, and lets
 * finance settle an approved claim OUT OF BAND (off-cycle mark-paid: method +
 * reference both required — a record that payment happened, not a payout rail).
 *
 * Admin-layout only (HR/admin/owner/finance); the layout beforeLoad bounces
 * plain employees. The list endpoint is itself HR/finance-gated server-side.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CurrencyCode } from '@kora/types';
import { authedLayoutRoute } from './authed.js';
import {
  listClaims,
  markPaidOffcycle,
  type ExpenseClaim,
  type ExpenseClaimStatus,
} from '../api/expense.js';
import { Alert } from '../components/Alert.js';
import { ApiErrorNotice, apiErrorMessage } from '../components/ApiErrorNotice.js';
import { formatDate, formatMoney } from '../lib/format.js';
import { ClaimReceipts, StatusPill, reimbursementLabel } from './expenseShared.js';

export const expensePortfolioRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/expense/portfolio',
  component: ExpensePortfolioPage,
});

const FILTERS: { label: string; statuses: ExpenseClaimStatus[] }[] = [
  { label: 'Awaiting reimbursement', statuses: ['approved'] },
  { label: 'Reimbursed', statuses: ['reimbursed'] },
  { label: 'Paid off-cycle', statuses: ['paid_offcycle'] },
  { label: 'All settled + pending', statuses: ['approved', 'reimbursed', 'paid_offcycle'] },
];

function ExpensePortfolioPage(): JSX.Element {
  const [filterIdx, setFilterIdx] = useState(0);
  const filter = FILTERS[filterIdx]!;
  const query = useQuery({
    queryKey: ['expense', 'portfolio', filter.statuses.join(',')],
    queryFn: () => listClaims(filter.statuses),
  });
  const claims = query.data?.claims ?? [];

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Expense portfolio</h1>
        <p className="text-sm text-gray-400">
          Approved reimbursements are swept onto the next payroll run automatically.
          For an urgent or off-cycle case, settle it out-of-band here and record how.
        </p>
      </header>

      <nav className="flex gap-2 border-b border-gray-900 flex-wrap">
        {FILTERS.map((f, i) => (
          <TabButton key={f.label} active={i === filterIdx} onClick={() => setFilterIdx(i)}>
            {f.label}
          </TabButton>
        ))}
      </nav>

      {query.isLoading ? (
        <Placeholder>Loading…</Placeholder>
      ) : query.isError ? (
        <Placeholder error>Could not load the portfolio.</Placeholder>
      ) : claims.length === 0 ? (
        <Placeholder>No claims in this view.</Placeholder>
      ) : (
        <div className="space-y-3">
          {claims.map((c) => (
            <PortfolioRow key={c.id} claim={c} />
          ))}
        </div>
      )}
    </section>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm border-b-2 ${
        active ? 'border-white text-white' : 'border-transparent text-gray-400 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function PortfolioRow({ claim }: { claim: ExpenseClaim }): JSX.Element {
  const queryClient = useQueryClient();
  const [settling, setSettling] = useState(false);
  const name = claim.employee ? `${claim.employee.firstName} ${claim.employee.lastName}` : claim.employeeId;
  const reimb = reimbursementLabel(claim);

  const markPaid = useMutation({
    mutationFn: (body: { method: string; reference: string }) => markPaidOffcycle(claim.id, body),
    onSuccess: async () => {
      setSettling(false);
      await queryClient.invalidateQueries({ queryKey: ['expense', 'portfolio'] });
    },
  });

  return (
    <div className="rounded border border-gray-900 p-4 space-y-2">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-white">
            {name}
            {claim.employee ? (
              <span className="text-gray-500 font-mono text-xs ml-2">{claim.employee.employeeNumber}</span>
            ) : null}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {claim.category} · incurred {formatDate(claim.incurredDate)}
          </p>
          <p className="text-sm text-gray-300 mt-1">{claim.description}</p>
        </div>
        <div className="text-right space-y-1">
          <p className="text-sm text-white font-mono">{formatMoney(claim.amount, claim.currency as CurrencyCode)}</p>
          <StatusPill status={claim.status} />
          {reimb ? <p className="text-[0.65rem] text-gray-500">{reimb}</p> : null}
        </div>
      </header>

      <ClaimReceipts claimId={claim.id} canModify={false} />

      {claim.status === 'approved' ? (
        <button
          type="button"
          onClick={() => setSettling(true)}
          className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900"
        >
          Mark paid off-cycle
        </button>
      ) : null}
      <ApiErrorNotice error={markPaid.error} fallback="Could not record the payment." compact />

      <MarkPaidModal
        open={settling}
        isSubmitting={markPaid.isPending}
        errorMessage={apiErrorMessage(markPaid.error, 'Could not record the payment.')}
        onCancel={() => setSettling(false)}
        onConfirm={(method, reference) => markPaid.mutate({ method, reference })}
      />
    </div>
  );
}

/** Off-cycle settlement — both method AND reference are required (finance's
 *  money-out action; a bare "paid" with no trail isn't good enough). */
function MarkPaidModal({
  open,
  isSubmitting,
  errorMessage,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onConfirm: (method: string, reference: string) => void;
}): JSX.Element | null {
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  if (!open) return null;
  const disabled = method.trim().length < 1 || reference.trim().length < 1 || isSubmitting;
  const inputClass =
    'w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm focus:border-white focus:outline-none';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="markpaid-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
    >
      <div className="w-full max-w-md rounded border border-gray-800 bg-gray-950 p-6 space-y-4">
        <header className="space-y-1">
          <h2 id="markpaid-title" className="font-serif text-xl text-white">
            Record off-cycle payment
          </h2>
          <p className="text-sm text-gray-400">
            This records that the claim was settled outside payroll — it does not
            move money. It's removed from the payroll sweep.
          </p>
        </header>
        <div className="space-y-1">
          <label htmlFor="markpaid-method" className="block text-xs uppercase tracking-wide text-gray-400">
            Method *
          </label>
          <input
            id="markpaid-method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            placeholder="e.g. bank transfer"
            className={inputClass}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="markpaid-reference" className="block text-xs uppercase tracking-wide text-gray-400">
            Reference *
          </label>
          <input
            id="markpaid-reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g. TXN-12345"
            className={inputClass}
          />
        </div>
        {errorMessage ? <Alert variant="error">{errorMessage}</Alert> : null}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded border border-gray-700 px-4 py-2 text-sm hover:bg-gray-900 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(method.trim(), reference.trim())}
            disabled={disabled}
            className="rounded bg-white px-4 py-2 text-sm font-medium text-black hover:bg-gray-200 disabled:opacity-50"
          >
            {isSubmitting ? 'Working…' : 'Record payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return (
    <div
      className={`rounded border p-6 text-center text-sm ${
        error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'
      }`}
    >
      {children}
    </div>
  );
}
